import React, { useState } from "react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import { Typography, Checkbox, FormControlLabel } from "@material-ui/core";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import VehicleSummaryCard from "../VehicleSummaryCard";
import EmpresaSearch from "../../../AccessTypeForm/TransportContentForm/EmpresaSearch";
import { useTranslation } from "../../../../../../../hooks/useTranslation";
import Button from "../../../../../../../components/common/PL/Button/Button";
import FormTextInput from "../../../../../../../components/common/PL/TextInput/FormTextInput";
import FormSelect from "../../../../../../../components/common/PL/Select/FormSelect";
import useVehicleBrands from "../../../../../../../hooks/useVehicleBrands";
import useVehicleTypes from "../../../../../../../hooks/useVehicleTypes";
import useInsuranceCompanies from "../../../../../../../hooks/useInsuranceCompanies";
import useVehicleSearch from "../../../../../../../hooks/useVehicleSearch";
import { FindVehicleResult } from "../../../../../../../passapp-sdk/vehicle";

import FormDatePicker from "../../../../../../../components/common/PL/DatePicker/FormDatePicker";
import FormDocumentPreviewForm from "../../../../../../../components/common/PL/PicturePreview/FormDocumentPreviewForm";
import DialogConfirmation from "../../../../../../../components/common/PL/Dialog/DialogConfirmation";
import { useTransportAuthPrefill } from "../../../AccessTypeForm/TransportContentForm/useTransportAuthPrefill";
import { validateNotExpired } from "../../../../utils/registroIngresoSchema";
import {
  MAX_ASSOCIATED_VEHICLES,
  toTrailerVehicleFormValues,
} from "../../../../utils/trailerVehicleForm";
import useEvent from "../../../../hooks/useEvent";
import "./VehicleContentAssociatedVehicleDataForm.css";

/** Las patentes se comparan sin espacios ni mayúsculas: "AB 123 CD" y "ab123cd" son la misma. */
const normalizePlate = (plate: unknown): string =>
  String(plate ?? "")
    .replace(/\s+/g, "")
    .toUpperCase();

const samePlate = (a: unknown, b: unknown): boolean => {
  const left = normalizePlate(a);
  return !!left && left === normalizePlate(b);
};

/** Mismo filtro que aplica el input de patente (`extraType="plate"` en TextInput) al tipear: sin
 * esto, la patente buscada pasaba tal cual —minúsculas incluidas— al alta de un vehículo nuevo. */
const toPlateFieldValue = (raw: string): string => raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

const VehicleContentAssociatedVehicleDataForm: React.FC<{
  disabled?: boolean;
  isAccordion?: boolean;
}> = ({ disabled = false, isAccordion = false }) => {
  const { t, BundleKey } = useTranslation();
  const { control, watch, trigger, getValues, setValue } = useFormContext();
  const { brands: vehicleBrandOptions } = useVehicleBrands();
  const { vehicleTypes: vehicleTypeOptions } = useVehicleTypes();
  const { companies: insuranceCompanyOptions } = useInsuranceCompanies();
  const trailerTypeId = React.useMemo(
    () =>
      String(
        vehicleTypeOptions.find((option) => option.label.trim().toLowerCase().includes("acoplado"))
          ?.value ?? ""
      ),
    [vehicleTypeOptions]
  );
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "trailerVehicles",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isNewVehicle, setIsNewVehicle] = useState(false);
  const [initialVehicleData, setInitialVehicleData] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectError, setSelectError] = useState<string | null>(null);
  const { saveExistingTrailerVehicle } = useEvent();

  const personId = watch("id");
  const associatedVehiclesValue = watch("associatedVehicles");
  const knownVehicles: any[] = associatedVehiclesValue || [];
  const {
    results: vehicleSearchResults,
    selecting: selectingVehicle,
    selectVehicle: selectVehicleApi,
    findExact: findExactVehicle,
  } = useVehicleSearch(personId, searchValue);

  /** `selectVehicle` es autoritativo en cada llamada: reemplaza la entrada existente en vez de
   * conservar la primera copia vista en la sesión (ver el mismo helper en
   * VehicleContentUserDataForm). */
  const appendKnownVehicle = React.useCallback(
    (vehicle: any) => {
      const current = (getValues("associatedVehicles") as any[]) || [];
      const index = current.findIndex((v: any) => v.id === vehicle.id);
      const next =
        index === -1
          ? [...current, vehicle]
          : current.map((v: any, i: number) => (i === index ? vehicle : v));
      setValue("associatedVehicles", next);
    },
    [getValues, setValue]
  );

  // El acoplado de la autorización de transporte se refleja como un vehículo asociado más, marcado
  // con `fromAuthorization` para bloquear sólo la patente (igual que el vehículo principal). Si el
  // chofer ya lo tiene registrado se busca su ficha completa (findVehicles + selectVehicle) para
  // traer seguro y VTV; sin eso, sólo la patente aparecía en el segundo ingreso.
  const authPrefill = useTransportAuthPrefill();
  const authTrailerPlate = authPrefill?.trailerPlate ?? null;
  const [authTrailerVehicle, setAuthTrailerVehicle] = useState<any>(null);

  React.useEffect(() => {
    if (!authTrailerPlate) {
      setAuthTrailerVehicle(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const found = await findExactVehicle(authTrailerPlate);
      if (cancelled || !found) return;
      const result = await selectVehicleApi(found.id);
      if (cancelled || !result.success) return;
      appendKnownVehicle(result.vehicle);
      setAuthTrailerVehicle(result.vehicle);
    })();
    return () => {
      cancelled = true;
    };
  }, [authTrailerPlate, findExactVehicle, selectVehicleApi, appendKnownVehicle]);

  React.useEffect(() => {
    const current: any[] = getValues("trailerVehicles") || [];
    // Los que agregó el guardia a mano (o los que se sembraron desde el ingreso) se conservan
    // siempre.
    const manual = current.filter((v) => !v?.fromAuthorization);
    // Si el acoplado de la autorización ya está entre ellos, gana la fila existente: trae los datos
    // completos del vehículo y se puede editar y borrar, mientras que la de la autorización es sólo
    // la patente y va bloqueada. Además evita mostrar el mismo acoplado dos veces.
    const isAlreadyListed = manual.some((v) => samePlate(v?.vehiclePlate, authTrailerPlate));
    // La autorización cuenta dentro del máximo de dos unidades. Si el guardia ya cargó las dos a
    // mano no se descarta ninguna para hacer lugar: el límite se aplica al alta, no borrando datos.
    const next =
      authTrailerPlate && !isAlreadyListed && manual.length < MAX_ASSOCIATED_VEHICLES
        ? [
            {
              ...toTrailerVehicleFormValues(authTrailerVehicle, {
                vehiclePlate: authTrailerPlate,
                ...(authTrailerVehicle ? {} : { vehicleTypeId: trailerTypeId }),
                ...(authTrailerVehicle ? {} : { appliesVtv: true }),
              }),
              fromAuthorization: true,
            },
            ...manual,
          ]
        : manual;

    const isSame = JSON.stringify(next) === JSON.stringify(current);
    if (isSame) return;

    replace(next);
  }, [authTrailerPlate, authTrailerVehicle, trailerTypeId, getValues, replace]);

  const trailerVehiclesValues = watch("trailerVehicles") || [];
  const selectedVehicleId = watch("selectedVehicleId");
  const hasReachedAssociatedVehicleLimit =
    trailerVehiclesValues.length >= MAX_ASSOCIATED_VEHICLES;
  const validateNewTrailerPlate = React.useCallback(
    async (plate: string, index: number) => {
      const trailer = getValues(`trailerVehicles.${index}`);
      if (trailer?.id || !plate?.trim()) return true;

      const existingVehicle = await findExactVehicle(plate);
      return existingVehicle
        ? "Ya existe un vehiculo con esta patente. Seleccionalo desde el buscador."
        : true;
    },
    [findExactVehicle, getValues]
  );

  // Sólo las unidades nuevas de transporte arrancan como Acoplado. Los existentes no se alteran.
  React.useEffect(() => {
    if (!isAccordion || !trailerTypeId) return;
    trailerVehiclesValues.forEach((trailer: any, index: number) => {
      if (!trailer?.id && !trailer?.vehicleTypeId) {
        setValue(`trailerVehicles.${index}.vehicleTypeId`, trailerTypeId);
      }
    });
  }, [isAccordion, trailerTypeId, trailerVehiclesValues, setValue]);

  // No ofrecer de nuevo un vehículo ya elegido como principal o como otro acoplado.
  const searchOptions = vehicleSearchResults.filter((v) => {
    if (v.id === selectedVehicleId) return false;
    return !trailerVehiclesValues.some(
      (trailer: any) => trailer.id === v.id || samePlate(trailer.vehiclePlate, v.plate)
    );
  });
  const trimmedSearch = searchValue.trim();
  const hasExactMatch = searchOptions.some((v) => samePlate(v.plate, trimmedSearch));

  /** Igual que el vehículo principal: primero avisa la selección (obligatoria en cada elección) —
   * es lo que trae la ficha completa del acoplado y habilita sus fotos — recién con eso se agrega
   * a la lista. */
  const handleSelectExistingVehicle = async (candidate: FindVehicleResult) => {
    if (
      disabled ||
      selectingVehicle ||
      (getValues("trailerVehicles") || []).length >= MAX_ASSOCIATED_VEHICLES
    )
      return;
    setSelectError(null);
    const result = await selectVehicleApi(candidate.id);
    if (!result.success) {
      setSelectError(result.error);
      return;
    }
    appendKnownVehicle(result.vehicle);
    const formValues = toTrailerVehicleFormValues(result.vehicle);
    append(formValues);
    // Igual que el vehÃ­culo principal, un resultado de findVehicles se abre de inmediato
    // con los datos completos devueltos por selectVehicle, listo para revisar o editar.
    setIsNewVehicle(false);
    setInitialVehicleData(formValues);
    setEditingIndex(fields.length);
    setSearchValue("");
  };

  // Igual que en el formulario del vehículo principal: si esto se engancha a un `onClick` el
  // argumento es el evento de React, así que sólo aceptamos strings como patente inicial.
  const handleAddVehicle = (searchText: unknown = "") => {
    if (
      disabled ||
      (getValues("trailerVehicles") || []).length >= MAX_ASSOCIATED_VEHICLES
    )
      return;
    append(
      toTrailerVehicleFormValues(null, {
        vehiclePlate: typeof searchText === "string" ? toPlateFieldValue(searchText) : "",
        vehicleTypeId: trailerTypeId,
        appliesVtv: true,
      })
    );
    setIsNewVehicle(true);
    setInitialVehicleData(null);
    setEditingIndex(fields.length);
    setSearchValue("");
  };

  const handleStartEdit = (index: number) => {
    if (disabled) return;
    setInitialVehicleData(getValues(`trailerVehicles.${index}`));
    setIsNewVehicle(false);
    setEditingIndex(index);
  };

  const handleSave = async (index: number) => {
    if (disabled) return;
    setSaveError(null);
    const isValid = await trigger(`trailerVehicles.${index}`, { shouldFocus: true });
    if (isValid) {
      const trailer = getValues(`trailerVehicles.${index}`);
      const oldVehicle = knownVehicles.find((vehicle: any) => vehicle.id === trailer?.id);
      if (oldVehicle) {
        setSavingIndex(index);
        const result = await saveExistingTrailerVehicle(trailer, oldVehicle);
        setSavingIndex(null);
        if (!result.success) {
          setSaveError(result.error || "No se pudo guardar el vehículo asociado.");
          return;
        }
        if (result.vehicle) appendKnownVehicle(result.vehicle);
      }
      setEditingIndex(null);
      setIsNewVehicle(false);
      setInitialVehicleData(null);
    }
  };

  const handleCancel = (index: number) => {
    if (isNewVehicle) {
      remove(index);
    } else if (initialVehicleData) {
      setValue(`trailerVehicles.${index}`, initialVehicleData);
    }
    setEditingIndex(null);
    setIsNewVehicle(false);
    setInitialVehicleData(null);
  };

  const handleDeleteClick = (index: number) => {
    if (disabled) return;
    setDeleteIndex(index);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex !== null) {
      remove(deleteIndex);
    }
    setIsDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const getVehicleRequired = (message: string) => ({
    required: message,
  });

  return (
    <div className="vehicleAssociatedVehicleSection">
      <div className="vehicleAssociatedVehicleSectionHeader">
        <Typography style={{ fontWeight: 700, fontSize: 20 }}>
          {t(BundleKey.DETAIL_ASSOCIATED_VEHICLES_TITLE)}
        </Typography>
      </div>

      {/* Igual que el vehículo principal: el buscador queda siempre visible mientras haya lugar
          (hasta MAX_ASSOCIATED_VEHICLES) — acá, a diferencia de ahí, no se reemplaza por una tarjeta
          al elegir uno: puede haber más de un acoplado, así que se puede seguir agregando. Se oculta
          sólo mientras se edita otra fila, para no tener dos formularios abiertos a la vez. */}
      {!disabled && editingIndex === null && !hasReachedAssociatedVehicleLimit && (
        <>
          <EmpresaSearch
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            selectedEmpresa={null}
            onSelect={handleSelectExistingVehicle}
            onClear={() => setSearchValue("")}
            options={searchOptions}
            getLabel={(vehicle: FindVehicleResult) =>
              [vehicle.plate, vehicle.brand].filter(Boolean).join(" - ")
            }
            placeholder={t(BundleKey.VEHICLE_SEARCH_PLACEHOLDER)}
            icon={<DirectionsCarIcon fontSize="small" />}
            emptyLabel={
              trimmedSearch.length < 3
                ? "Ingresá al menos 3 caracteres de la patente"
                : "No se encontraron vehículos con esa patente"
            }
            addNewLabel="Agregar vehículo"
            onAddNew={trimmedSearch.length >= 3 && !hasExactMatch ? handleAddVehicle : undefined}
            disabled={disabled || selectingVehicle}
            loading={selectingVehicle}
            filterLocally={false}
          />
          {selectError && <div className="vehicleAssociatedVehicleSaveError">{selectError}</div>}
        </>
      )}

      <div className="vehicleAssociatedVehicleList">
        {fields.map((field, index) => {
          const isEditing = editingIndex === index;
          const vehicleData = trailerVehiclesValues[index] || {};
          const plate =
            vehicleData.vehiclePlate || t(BundleKey.DETAIL_ASSOCIATED_VEHICLES_ITEM_TITLE);
          const vehicleBrand = vehicleData.vehicleBrand;
          const vehicleColor = vehicleData.vehicleColor;
          const vehicleInfo = [vehicleBrand, vehicleColor].filter(Boolean).join(" - ");

          const isFromAuthorization = !!vehicleData.fromAuthorization;

          if (!isEditing) {
            return (
              <VehicleSummaryCard
                key={field.id}
                plate={plate}
                vehicleInfo={vehicleInfo}
                isActionsDisabled={disabled || editingIndex !== null}
                onEdit={() => handleStartEdit(index)}
                onDelete={() => handleDeleteClick(index)}
              />
            );
          }

          return (
            <div
              key={field.id}
              className="vehicleAssociatedVehicleSubcard vehicleAssociatedVehicleItem"
            >
              <div className="vehicleAssociatedVehicleSubcardHeader">
                {/* Mismo tamaño que el título "Vehículos asociados" de arriba (fontSize: 20): con
                    `variant="h5"` sin overridear el tamaño, este heredaba el h5 del theme de MUI,
                    bastante más grande, y quedaban desparejos entre sí. */}
                <Typography style={{ fontWeight: 700, fontSize: 20 }}>
                  {`${t(BundleKey.DETAIL_ASSOCIATED_VEHICLES_ITEM_TITLE)} ${index + 1}`}
                </Typography>
              </div>

              <div className="vehicleAssociatedVehicleFieldsGrid">
                <FormSelect
                  name={`trailerVehicles.${index}.vehicleTypeId`}
                  label="Tipo de vehículo *"
                  placeholder="-completar-"
                  options={vehicleTypeOptions}
                  rules={getVehicleRequired("El tipo de vehículo es obligatorio")}
                  disabled={disabled}
                  isSearchable
                />
                <FormTextInput
                  name={`trailerVehicles.${index}.vehiclePlate`}
                  label={`${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_PLATE)} *`}
                  placeholder="-completar-"
                  extraType="plate"
                  rules={{
                    ...getVehicleRequired("La patente es obligatoria"),
                    validate: (plate: string) => validateNewTrailerPlate(plate, index),
                  }}
                  disabled={disabled || isFromAuthorization || !!vehicleData.id}
                />
                <FormSelect
                  name={`trailerVehicles.${index}.vehicleBrand`}
                  label={`${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_BRAND)} *`}
                  placeholder="-completar-"
                  options={vehicleBrandOptions}
                  rules={getVehicleRequired("La marca es obligatoria")}
                  isSearchable
                />
              </div>

              <div className="vehicleFieldsSubsection">
                <div className="vehicleFieldsSubsectionHeader">
                  <span className="vehicleFieldsSubsectionTitle">VTV</span>
                  <Controller
                    name={`trailerVehicles.${index}.appliesVtv`}
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              // Sin VTV no puede quedar una fecha vieja colgada.
                              if (!e.target.checked) {
                                setValue(`trailerVehicles.${index}.vtvExpirationDate`, null);
                              }
                            }}
                            disabled={disabled}
                          />
                        }
                        label="Aplica VTV"
                      />
                    )}
                  />
                </div>
                {!!trailerVehiclesValues[index]?.appliesVtv && (
                  <div className="vehicleAssociatedVehicleDocumentsGrid">
                    <FormDatePicker
                      name={`trailerVehicles.${index}.vtvExpirationDate`}
                      label="Fecha de vencimiento VTV *"
                      placeholder="-completar-"
                      variant="pl"
                      rules={{
                        ...getVehicleRequired("La fecha de vencimiento de VTV es obligatoria"),
                        validate: validateNotExpired,
                      }}
                    />
                    <FormDocumentPreviewForm
                      name={`trailerVehicles.${index}.vtvPhoto`}
                      label="Foto VTV"
                      fileName="Foto VTV."
                      compact
                      displayVariant="file"
                      readOnly={disabled}
                    />
                  </div>
                )}
              </div>

              <div className="vehicleFieldsSubsection">
                <span className="vehicleFieldsSubsectionTitle">Seguro</span>
                <div className="vehicleAssociatedVehicleFieldsGrid">
                  <FormSelect
                    name={`trailerVehicles.${index}.insuranceCompany`}
                    label="Aseguradora *"
                    placeholder="-completar-"
                    options={insuranceCompanyOptions}
                    rules={getVehicleRequired("La aseguradora es obligatoria")}
                    isSearchable
                  />
                  <FormTextInput
                    name={`trailerVehicles.${index}.policyNumber`}
                    label="Póliza n° *"
                    placeholder="-completar-"
                    extraType="alfanumericNoSpaces"
                    rules={getVehicleRequired("El número de póliza es obligatorio")}
                  />
                </div>
                {/* Propia grilla, como en el vehículo principal: así fecha + foto quedan siempre
                    emparejadas en la misma fila, sin depender de los dos campos de arriba. */}
                <div className="vehicleAssociatedVehicleDocumentsGrid">
                  <FormDatePicker
                    name={`trailerVehicles.${index}.insuranceExpirationDate`}
                    label={`${t(BundleKey.DETAIL_ENTRYMETHOD_CAR_INSURANCE_DATE)} *`}
                    placeholder="-completar-"
                    variant="pl"
                    rules={{
                      ...getVehicleRequired("La fecha de vencimiento es obligatoria"),
                      validate: validateNotExpired,
                    }}
                  />
                  <FormDocumentPreviewForm
                    name={`trailerVehicles.${index}.insurancePhoto`}
                    label="Foto seguro"
                    fileName="Foto seguro."
                    compact
                    displayVariant="file"
                    readOnly={disabled}
                  />
                </div>
              </div>

              <div className="vehicleAssociatedVehicleActions">
                <div className="vehicleAssociatedVehicleActionsSpacer" />
                <Button
                  className="vehicleAssociatedVehicleActionButton"
                  variant="outline"
                  onClick={() => handleCancel(index)}
                  type="button"
                >
                  {t(BundleKey.ACTION_CANCEL)}
                </Button>
                <Button
                  className="vehicleAssociatedVehicleActionButton"
                  onClick={() => handleSave(index)}
                  type="button"
                  loading={savingIndex === index}
                  disabled={savingIndex === index}
                >
                  {t(BundleKey.ACTION_SAVE)}
                </Button>
              </div>
              {saveError && editingIndex === index && (
                <div className="vehicleAssociatedVehicleSaveError">{saveError}</div>
              )}
            </div>
          );
        })}
      </div>

      <DialogConfirmation
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Eliminar vehículo"
        message={
          deleteIndex !== null ? (
            <>
              Esta seguro que quiere eliminar el vehículo con patente{" "}
              <strong>
                {trailerVehiclesValues[deleteIndex]?.vehiclePlate ||
                  t(BundleKey.DETAIL_ASSOCIATED_VEHICLES_ITEM_TITLE)}
              </strong>
              ?
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Eliminar"
        cancelLabel={t(BundleKey.ACTION_CANCEL)}
      />
    </div>
  );
};

export default VehicleContentAssociatedVehicleDataForm;
